import { Control, Controller, RegisterOptions } from "react-hook-form";
import { ChangeEvent, useCallback, useMemo } from "react";
import { Button } from "@/components/shadcn/button";

import { NoxusInput } from "@/components/Form/InputField";
import { findMaxStringValue } from "@/utils/generic";
import { Plus, Trash } from "lucide-react";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { cn } from "@/lib/utils";

interface IDictListInputProps {
  title?: string;
  value: Record<string, string>[];
  tooltip?: string;
  onChange: (value: any[]) => void;
  keyLabel: string;
  valueLabel: string;
  keyPrefix: string;
  valuePlaceholder: string;
  required_keys?: string[];
  isDisabled?: boolean;
}

const DictListInput = (props: IDictListInputProps) => {
  const {
    value,
    keyLabel,
    valueLabel,
    keyPrefix,
    valuePlaceholder,
    required_keys,
    isDisabled,
  } = props;

  const cantDelete = useMemo(
    () => value.length <= 1 || !!isDisabled,
    [value, isDisabled]
  );

  const handleAddClick = useCallback(() => {
    if (isDisabled) return;
    const maxOutputValue = findMaxStringValue(
      props.value.map((i) => i[keyLabel]),
      new RegExp(`^${keyPrefix} (\\d+)$`)
    );
    const newDictArray = [
      ...props.value,
      { [keyLabel]: `${keyPrefix} ${maxOutputValue + 1}`, [valueLabel]: "" },
    ];
    props.onChange(newDictArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyPrefix, props, isDisabled]);

  const handleDeleteClick = useCallback(
    (indexToRemove: number) => {
      if (
        cantDelete ||
        required_keys?.includes(props.value[indexToRemove][keyLabel]) ||
        isDisabled
      )
        return;

      const newDictArray = [
        ...props.value
          .slice(0, indexToRemove)
          .concat(props.value.slice(indexToRemove + 1)),
      ];
      props.onChange(newDictArray);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cantDelete, props, isDisabled]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, key: string, index: number) => {
      if (isDisabled) return;
      const newDictArray = [...props.value];
      newDictArray[index][key] = event.target.value;
      props.onChange(newDictArray);
    },
    [props, isDisabled]
  );

  return (
    <>
      <h5 className="text-primary font-medium text-md mb-4">{props.title}</h5>
      <div className="gap-6 flex flex-col items-start">
        {props.value.map((dict: any, index: number) => (
          <div
            key={`dict-${index}`}
            className="w-full gap-4 flex flex-col items-end relative border border-border rounded-md p-4"
          >
            <NoxusInput
              key={`dict-${index}-${keyLabel}`}
              value={dict[keyLabel]}
              label={keyLabel}
              forcedName={"Category required"}
              onChange={(e) => handleChange(e, keyLabel, index)}
              placeholder={keyPrefix}
              disable={required_keys?.includes(dict[keyLabel]) || isDisabled}
            />
            <NoxusInput
              key={`dict-${index}-${valueLabel}`}
              value={dict[valueLabel]}
              label={valueLabel}
              onChange={(e) => handleChange(e, valueLabel, index)}
              placeholder={valuePlaceholder}
              isTextarea
              isAutoResize
              customStyle="max-h-80"
              disable={required_keys?.includes(dict[keyLabel]) || isDisabled}
            />

            <div className="absolute -top-3 -right-3">
              <NoxusTooltip
                placement="left"
                content={
                  cantDelete
                    ? "Can't have less than 1 value"
                    : required_keys?.includes(dict[keyLabel])
                      ? "This category is required"
                      : ""
                }
              >
                <Button
                  variant="outline"
                  size="iconsm"
                  className="rounded-full disabled:opacity-100"
                  isDisabled={
                    cantDelete ||
                    required_keys?.includes(dict[keyLabel]) ||
                    isDisabled
                  }
                  onClick={() => handleDeleteClick(index)}
                >
                  <Trash
                    className={cn(
                      "h-2 w-2",
                      (cantDelete ||
                        required_keys?.includes(dict[keyLabel]) ||
                        isDisabled) &&
                        "opacity-25"
                    )}
                    strokeWidth="1.33px"
                  />
                </Button>
              </NoxusTooltip>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          onClick={handleAddClick}
          className="mt-2 mx-auto"
          isDisabled={isDisabled}
        >
          <Plus size="1.25rem" strokeWidth="1.33px" />
          {`Add ${props.keyPrefix?.toLowerCase() || "more"}`}
        </Button>
      </div>
    </>
  );
};

interface IDictListFieldProps {
  name: string;
  tooltip?: string;
  label?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  defaultValue?: Record<string, string>[];
  keyLabel: string;
  valueLabel: string;
  keyPrefix: string;
  valuePlaceholder: string;
  required_keys?: string[];
  isDisabled?: boolean;
}

export const DictListField = (props: IDictListFieldProps) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <DictListInput
            value={value ?? props.defaultValue ?? []}
            title={props.label} // this value will be used on the Add more button
            tooltip={props.tooltip}
            onChange={onChange}
            keyLabel={props.keyLabel}
            valueLabel={props.valueLabel}
            keyPrefix={props.keyPrefix}
            valuePlaceholder={props.valuePlaceholder}
            required_keys={props.required_keys}
            isDisabled={props.isDisabled}
          />
        );
      }}
    />
  );
};
